<template>
  <div ref="chatConversation" class="chat-conversation">
    <div
      ref="chatScrollButton"
      @click="scrollToBottom"
      class="chat-conversation__button"
      role="button"
    >
      <span class="chat-conversation__icon">
        <arrow-down-icon />
      </span>
    </div>
    <vue-scroll ref="scrollBody" @handle-scroll="handleScroll">
      <div ref="chatBody" class="chat-conversation__body">
        <div class="chat-conversation__filler"></div>
        <chat-loader v-if="showLoader" />
        <message-item v-if="showTopReachedMessage" :message="getTopMessage()" />
        <template v-for="(message, index) in messages">
          <message-item
            :key="message.id ? message.id + index : message.tempId + index"
            :message="message"
            :has-next="
              hasNextMessage(message.typeUser, index + 1) ||
              showDateHeader(message.createdAt, index + 1)
            "
          />
          <message-item
            :key="`d-${message.id + index}`"
            :message="getDateHeaderMessage(index + 1)"
            :has-next="hasNextMessage(message.typeUser, index + 1)"
            v-if="showDateHeader(message.createdAt, index + 1)"
          />
          <div
            ref="unreadMark"
            :key="`u-${message.id}`"
            v-if="showUnreadHeader(message.typeUser, message.readAt, index + 1)"
            class="message__mark"
          ></div>
        </template>
      </div>
    </vue-scroll>
  </div>
</template>

<script>
import { gsap } from 'gsap';
import { mapGetters } from 'vuex';
import {
  AUDIO_STOP_ALL,
  CHAT_BODY_SCROLL_TO_BOTTOM,
  CHAT_INPUT_FOCUS,
  CHAT_MULTIMEDIA_TOGGLE,
  CHAT_EMOJI_TOGGLE,
  CONVERSATION_LOAD_MESSAGES,
  MESSAGES_LOADED,
} from '@/eventTypes';
import ArrowDownIcon from '@/assets/icons/arrow-down.svg';
import EventBus from '@/eventBus';
import markAsReadGql from '@/graphql/mutations/markAsRead.gql';

export default {
  components: {
    ArrowDownIcon,
    ChatLoader: () => import('@/components/ChatLoader.vue'),
    MessageItem: () => import('@/components/MessageItem.vue'),
  },
  props: {
    clientId: {
      required: true,
      type: String,
    },
    messages: {
      required: true,
      type: Array,
    },
  },
  data() {
    return {
      animationScrollButton: {
        duration: 0.35,
        fromVars: {
          y: 21,
          opacity: 0,
          display: 'none',
        },
        toVars: {
          y: 0,
          opacity: 1,
          display: 'flex',
        },
      },
      scrollTop: 0,
      scrollHeight: 0,
      scrollProcess: 0,
      firstLoad: true,
    };
  },
  computed: {
    ...mapGetters([
      'chatIsLoading',
      'chatFirstLoad',
      'chatTopReached',
      'chatOldestMessageId',
      'chatHistoryOldestMessageId',
    ]),
    showLoader() {
      return this.chatIsLoading(this.clientId);
    },
    scrollFromBottom() {
      return this.scrollHeight - this.scrollTop;
    },
    showScrollButton() {
      return this.scrollFromBottom >= 400;
    },
    totalMessages() {
      return this.messages.length;
    },
    fromClient() {
      return this.messages.filter((m) => m.typeUser === 'Client');
    },
    totalFromClient() {
      return this.fromClient.length;
    },
    unreadTotal() {
      return this.fromClient.filter((m) => m.readAt === null).length;
    },
    lastMessageFromClient() {
      if (this.totalFromClient > 0) {
        return this.fromClient[this.totalFromClient - 1];
      }

      return null;
    },
    showTopReachedMessage() {
      return this.chatTopReached(this.clientId);
    },
  },
  watch: {
    clientId() {
      this.clientUpdated();
    },
    scrollTop(newValue, oldValue) {
      if (newValue <= 200 && newValue < oldValue) {
        this.loadMessages();
      }
    },
    scrollProcess(newValue) {
      if (newValue === 1 && !this.firstLoad) {
        this.doMarkAsRead();
      }
    },
    showScrollButton(newValue) {
      if (this.$refs.chatScrollButton) {
        if (newValue) {
          gsap.fromTo(this.$refs.chatScrollButton,
            this.animationScrollButton.duration,
            this.animationScrollButton.fromVars,
            this.animationScrollButton.toVars);
        } else {
          gsap.fromTo(this.$refs.chatScrollButton,
            this.animationScrollButton.duration,
            this.animationScrollButton.toVars,
            this.animationScrollButton.fromVars);
        }
      }
    },
    totalMessages() {
      if (this.scrollProcess === 0 && this.firstLoad) {
        this.firstLoad = false;
        setTimeout(() => this.scrollToBottom(), 200);
      }

      if (this.scrollProcess === 1) {
        this.doMarkAsRead();
        this.scrollToBottom();
      }
    },
  },
  created() {
    EventBus.$on(CHAT_BODY_SCROLL_TO_BOTTOM, () => {
      this.scrollToBottom();
    });

    EventBus.$on(MESSAGES_LOADED, (clientId) => {
      if (this.clientId === clientId) {
        this.keepScrollAt(this.scrollFromBottom);
      }
    });
  },
  destroyed() {
    EventBus.$off(CHAT_BODY_SCROLL_TO_BOTTOM);
    EventBus.$off(MESSAGES_LOADED);
  },
  mounted() {
    this.clientUpdated();
  },
  methods: {
    clientUpdated() {
      this.hidePanels();
      this.stopAudio();

      if (this.chatFirstLoad(this.clientId)) {
        this.loadMessages();
      }

      this.scrollHeight = 0;
      this.validateScrolling();
      this.forceReadIfNoScroll();

      EventBus.$emit(CHAT_INPUT_FOCUS);
    },
    hasNextMessage(typeUser, nextIndex) {
      return this.messages[nextIndex] && this.messages[nextIndex].typeUser === typeUser;
    },
    isSame(date1, date2, granularity) {
      if (date1 && date2) {
        const d2 = this.$moment(date2).local();
        return this.$moment(date1).local().isSame(d2, granularity);
      }

      return false;
    },
    showDateHeader(createdAt, nextIndex) {
      if (this.messages[nextIndex]) {
        return createdAt && this.messages[nextIndex].createdAt
          && !this.isSame(this.messages[nextIndex].createdAt, createdAt, 'day');
      }

      return false;
    },
    getDateHeaderMessage(nextIndex) {
      let relativeTimestamp = '';

      if (this.messages[nextIndex]) {
        relativeTimestamp = this.messages[nextIndex].createdAt;
      }

      return {
        relativeTimestamp,
        hideTimestamp: true,
        typeUser: null,
      };
    },
    getTopMessage() {
      return {
        text: this.$t('chat.top-reached'),
        hideTimestamp: true,
        typeUser: null,
      };
    },
    showUnreadHeader(typeUser, readAt, nextIndex) {
      if (this.messages[nextIndex]) {
        const nextMsg = this.messages[nextIndex];

        return (
          typeUser === 'User'
          && nextMsg.typeUser === 'Client'
          && nextMsg.readAt === null
        ) || (
          typeUser === 'Client'
          && readAt !== null
          && nextMsg.typeUser === 'Client'
          && nextMsg.readAt === null
        );
      }

      return false;
    },
    handleScroll(v) {
      this.scrollTop = v.scrollTop;
      this.scrollProcess = v.process;
      this.updateScrollHeight();
    },
    updateScrollHeight() {
      const scrollHeight = this.$refs.chatBody ? this.$refs.chatBody.scrollHeight : 0;
      const clientHeight = this.$refs.chatConversation
        ? this.$refs.chatConversation.clientHeight
        : 0;

      this.scrollHeight = scrollHeight - clientHeight;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.scrollBody) {
          this.$refs.scrollBody.scrollTo({ y: '100%' }, 0);
        }
      });
    },
    scrollToPosition(position) {
      this.$nextTick(() => {
        if (this.$refs.scrollBody) {
          this.$refs.scrollBody.scrollTo({ y: position }, 0);
        }
      });
    },
    validateScrolling() {
      this.$nextTick(() => {
        if (this.$refs.unreadMark && this.$refs.unreadMark.length > 0) {
          const mark = this.$refs.unreadMark[0];
          const position = mark.offsetTop;

          this.scrollToPosition(position - 5);
        } else {
          this.scrollToBottom();
        }
      });
    },
    keepScrollAt(initialY) {
      if (this.$refs.scrollBody) {
        this.$refs.scrollBody.refresh();
      }

      this.$nextTick(() => {
        const scrollHeight = this.$refs.chatBody ? this.$refs.chatBody.scrollHeight : 0;
        const clientHeight = this.$refs.chatConversation
          ? this.$refs.chatConversation.clientHeight
          : 0;
        const newY = scrollHeight - clientHeight - initialY;

        this.scrollToPosition(newY);
      });
    },
    loadMessages() {
      if (!this.chatIsLoading(this.clientId) && !this.chatTopReached(this.clientId)) {
        const oldestId = !this.isHistory
          ? this.chatOldestMessageId(this.clientId)
          : this.chatHistoryOldestMessageId(this.clientId);
        EventBus.$emit(CONVERSATION_LOAD_MESSAGES, this.clientId, oldestId);
      }
    },
    doMarkAsRead() {
      if ((this.lastMessageFromClient !== null
        && this.lastMessageFromClient.readAt === null)
        || this.unreadTotal > 0) {
        this.markMessagesAsRead(this.lastMessageFromClient.id);
      }
    },
    async markMessagesAsRead(msgId) {
      await this.$apollo.mutate({
        mutation: markAsReadGql,
        variables: {
          id: msgId,
        },
      });
    },
    hidePanels() {
      EventBus.$emit(CHAT_MULTIMEDIA_TOGGLE, false);
      EventBus.$emit(CHAT_EMOJI_TOGGLE, false);
    },
    stopAudio() {
      EventBus.$emit(AUDIO_STOP_ALL);
    },
    forceReadIfNoScroll() {
      setTimeout(() => {
        if (this.unreadTotal > 0 && this.scrollProcess === 0) {
          this.doMarkAsRead();
        }
      }, 1000);
    },
  },
};
</script>
